import { genesis002AssetsByToken } from "../viewer/genesis002AssetCatalog";
import { genesis002Catalog } from "../viewer/genesis002Catalog";
import "./storefront.css";

const COLLECTION_ID = "kowry-net-genesis-002";
const ITEMS_PER_PAGE = 20;

interface StorefrontItem {
  collectionId: string;
  itemId: string;
  tokenNumber: number;
  name: string;
  objectType: "kowry";
  thumbnail: string;
  mintState: "minted" | "unminted";
  saleState: "unknown";
}

const items: readonly StorefrontItem[] = genesis002Catalog.items.map((item) => ({
  collectionId: genesis002Catalog.collectionId,
  itemId: String(item.tokenNumber).padStart(4, "0"),
  tokenNumber: item.tokenNumber,
  name: item.name,
  objectType: "kowry",
  thumbnail: `/thumbnails/${COLLECTION_ID}/${String(item.tokenNumber).padStart(4, "0")}.webp`,
  mintState: genesis002AssetsByToken.has(item.tokenNumber) ? "minted" : "unminted",
  saleState: "unknown",
}));

redirectLegacyDeepLink();
connectGallery();
connectCollectionSurprise();
connectItemContext();

function redirectLegacyDeepLink(): void {
  if (window.location.pathname !== "/") return;
  const url = new URL(window.location.href);
  const requested = url.searchParams.get("kowry");
  if (!requested || !/^\d{1,4}$/.test(requested)) return;
  const tokenNumber = Number(requested);
  if (tokenNumber < 1 || tokenNumber > items.length) return;
  window.location.replace(itemPath(String(tokenNumber).padStart(4, "0")));
}

function connectGallery(): void {
  const app = document.getElementById("gallery-app");
  if (!app) return;
  const form = requireElement<HTMLFormElement>("gallery-filters");
  const grid = requireElement<HTMLElement>("gallery-grid");
  const summary = requireElement<HTMLElement>("gallery-summary");
  const pagination = requireElement<HTMLElement>("gallery-pagination");
  const surprise = requireElement<HTMLButtonElement>("surprise-me");

  const render = (): void => {
    const state = galleryStateFromUrl();
    writeFormState(form, state);
    const filtered = filteredItems(state);
    const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
    const page = Math.min(state.page, totalPages);
    if (page !== state.page) updateGalleryUrl({ ...state, page }, true);
    const visible = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

    summary.textContent = filtered.length
      ? `${filtered.length.toLocaleString()} objects · Page ${page} of ${totalPages}`
      : "No objects match this verified scope.";
    grid.innerHTML = visible.length ? visible.map((item, index) => cardMarkup(item, index)).join("") : emptyMarkup(state);
    pagination.innerHTML = paginationMarkup(page, totalPages, state);
  };

  form.addEventListener("change", () => {
    const data = new FormData(form);
    const sort = String(data.get("sort") ?? "catalog");
    updateGalleryUrl({
      collection: String(data.get("collection") ?? "all"),
      type: String(data.get("type") ?? "all"),
      mint: String(data.get("mint") ?? "all"),
      sale: String(data.get("sale") ?? "all"),
      sort,
      seed: sort === "random" ? randomSeed() : "",
      page: 1,
    });
    render();
  });
  pagination.addEventListener("click", (event) => {
    const link = (event.target as HTMLElement).closest<HTMLAnchorElement>("a[data-page]");
    if (!link) return;
    event.preventDefault();
    updateGalleryUrl({ ...galleryStateFromUrl(), page: Number(link.dataset.page) });
    render();
    app.scrollIntoView({ behavior: "smooth", block: "start" });
  });
  surprise.addEventListener("click", () => {
    const scope = filteredItems(galleryStateFromUrl());
    if (!scope.length) {
      summary.textContent = "No object is available in the selected scope. Adjust the filters and try again.";
      return;
    }
    const item = scope[cryptoRandomIndex(scope.length)];
    if (item) window.location.assign(withReturnContext(itemPath(item.itemId)));
  });
  window.addEventListener("popstate", render);
  render();
}

interface GalleryState {
  collection: string;
  type: string;
  mint: string;
  sale: string;
  sort: string;
  seed: string;
  page: number;
}

function galleryStateFromUrl(): GalleryState {
  const params = new URL(window.location.href).searchParams;
  return {
    collection: allowed(params.get("collection"), ["all", COLLECTION_ID], "all"),
    type: allowed(params.get("type"), ["all", "kowry"], "all"),
    mint: allowed(params.get("mint"), ["all", "minted", "unminted"], "all"),
    sale: allowed(params.get("sale"), ["all", "verified", "unknown"], "all"),
    sort: allowed(params.get("sort"), ["catalog", "random"], "catalog"),
    seed: params.get("seed")?.slice(0, 32) || "fractalar",
    page: Math.max(1, Number(params.get("page")) || 1),
  };
}

function filteredItems(state: GalleryState): StorefrontItem[] {
  const result = items.filter((item) =>
    (state.collection === "all" || item.collectionId === state.collection)
    && (state.type === "all" || item.objectType === state.type)
    && (state.mint === "all" || item.mintState === state.mint)
    && (state.sale === "all" || (state.sale === "unknown" && item.saleState === "unknown")),
  );
  if (state.sale === "verified") return [];
  if (state.sort === "random") result.sort((a, b) => seededRank(a.itemId, state.seed) - seededRank(b.itemId, state.seed));
  return result;
}

function cardMarkup(item: StorefrontItem, index: number): string {
  const mintLabel = item.mintState === "minted" ? "Minted" : "Not yet minted";
  const contextPath = withReturnContext(itemPath(item.itemId));
  return `<article class="object-card object-card--${item.mintState}">
    <a href="${contextPath}">
      <span class="object-card__visual"><img src="${item.thumbnail}" width="800" height="800" alt="${item.name}" ${index > 3 ? 'loading="lazy"' : ""}></span>
      <span class="object-card__body"><b>${item.name}</b><small>Genesis 002 · Kowry</small><span class="status-label status-label--${item.mintState}">${mintLabel}</span><span class="status-label status-label--unknown">Listing unknown</span></span>
    </a>
  </article>`;
}

function emptyMarkup(state: GalleryState): string {
  return `<div class="gallery-empty"><h3>Nothing verified here yet.</h3><p>${state.sale === "verified" ? "No supported source currently confirms an active listing. Fractalar will not infer availability from missing data." : "Try broadening the selected filters."}</p></div>`;
}

function paginationMarkup(page: number, totalPages: number, state: GalleryState): string {
  if (totalPages <= 1) return "";
  const previous = page > 1 ? pageLink("← Previous", page - 1, state) : '<span aria-disabled="true">← Previous</span>';
  const next = page < totalPages ? pageLink("Next →", page + 1, state) : '<span aria-disabled="true">Next →</span>';
  return `${previous}<span>Page ${page} of ${totalPages}</span>${next}`;
}

function pageLink(label: string, page: number, state: GalleryState): string {
  const url = galleryUrl({ ...state, page });
  return `<a href="${url}" data-page="${page}">${label}</a>`;
}

function updateGalleryUrl(state: GalleryState, replace = false): void {
  const url = galleryUrl(state);
  window.history[replace ? "replaceState" : "pushState"]({}, "", url);
}

function galleryUrl(state: GalleryState): string {
  const url = new URL(window.location.href);
  url.search = "";
  if (state.collection !== "all") url.searchParams.set("collection", state.collection);
  if (state.type !== "all") url.searchParams.set("type", state.type);
  if (state.mint !== "all") url.searchParams.set("mint", state.mint);
  if (state.sale !== "all") url.searchParams.set("sale", state.sale);
  if (state.sort !== "catalog") {
    url.searchParams.set("sort", state.sort);
    url.searchParams.set("seed", state.seed);
  }
  if (state.page > 1) url.searchParams.set("page", String(state.page));
  return `${url.pathname}${url.search}`;
}

function writeFormState(form: HTMLFormElement, state: GalleryState): void {
  for (const [name, value] of Object.entries(state)) {
    const field = form.elements.namedItem(name);
    if (field instanceof HTMLSelectElement) field.value = String(value);
  }
}

function connectCollectionSurprise(): void {
  document.getElementById("collection-surprise")?.addEventListener("click", () => {
    const item = items[cryptoRandomIndex(items.length)];
    if (item) window.location.assign(itemPath(item.itemId));
  });
}

function connectItemContext(): void {
  if (document.body.dataset.pageKind !== "item") return;
  const update = (tokenNumber: number): void => {
    const item = items[tokenNumber - 1];
    if (!item) return;
    const mint = document.getElementById("object-mint-state");
    if (mint) mint.textContent = item.mintState === "minted" ? "Minted · Approved origin recorded" : "Not yet minted";
    document.title = `${item.name} | Fractalar`;
  };
  const from = new URL(window.location.href).searchParams.get("from");
  const back = document.getElementById("back-to-gallery") as HTMLAnchorElement | null;
  if (back && from && from.startsWith("/") && !from.startsWith("//")) back.href = from;
  window.addEventListener("fractalar:itemchange", ((event: CustomEvent<number>) => update(event.detail)) as EventListener);
  const initial = Number(document.body.dataset.itemId);
  if (Number.isInteger(initial)) update(initial);
}

function itemPath(itemId: string): string {
  return `/collections/${COLLECTION_ID}/items/${itemId}/`;
}

function withReturnContext(path: string): string {
  const current = `${window.location.pathname}${window.location.search}`;
  return `${path}?from=${encodeURIComponent(current)}`;
}

function seededRank(value: string, seed: string): number {
  let hash = 2166136261;
  for (const char of `${seed}:${value}`) {
    hash ^= char.charCodeAt(0);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function cryptoRandomIndex(length: number): number {
  const value = new Uint32Array(1);
  crypto.getRandomValues(value);
  return value[0]! % length;
}

function randomSeed(): string {
  const value = new Uint32Array(1);
  crypto.getRandomValues(value);
  return value[0]!.toString(36);
}

function allowed(value: string | null, values: readonly string[], fallback: string): string {
  return value && values.includes(value) ? value : fallback;
}

function requireElement<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing required element: #${id}`);
  return element as T;
}
